import { useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  type BundleFile,
  type ExpertSummary,
  type MeetingStateResponse,
} from "../../api";
import { ExpertMark } from "../design/ExpertMark";
import { Icon } from "../design/Icon";
import {
  BundleTree,
  kindFromPath,
  type BundleTreeSelection,
} from "./BundleTree";
import { MdPreview } from "./MdPreview";
import { CsvPreview } from "./CsvPreview";
import { MermaidPreview } from "./MermaidPreview";
import { DiffPreview } from "./DiffPreview";

/** Renders the finalized meeting page — left-rail bundle tree + right-pane
 *  per-kind artifact preview. The tree is sourced from the dynamic
 *  `GET /v1/jobs/{id}/files` listing (no hardcoded substrate, no
 *  speculation about per-expert artifacts); the SPA now shows only files
 *  that actually exist in the zip. The `.recspec-bundle-body` /
 *  `.recspec-bundle-tree` className hooks wire the mobile.css collapse
 *  at ≤ 720 px (tree on top, content below in the same column).
 */
export function FinalizedView({
  state,
  experts,
}: {
  state: MeetingStateResponse;
  experts: ExpertSummary[];
}) {
  const myExperts = useMemo(() => {
    const byId = new Map(experts.map((e) => [e.id, e]));
    return state.expert_ids
      .map((pin) => pin.split("@")[0])
      .map((id) => byId.get(id))
      .filter((e): e is ExpertSummary => !!e);
  }, [experts, state.expert_ids]);

  // Fetch the bundle listing on mount / job-id change. While loading,
  // render a skeleton tree (empty rail) — once landed, pick a default
  // selection per the spec's preference order.
  const [files, setFiles] = useState<BundleFile[] | null>(null);
  const [listingError, setListingError] = useState<string | null>(null);
  useEffect(() => {
    if (!state.resolved_job_id) {
      setFiles(null);
      return;
    }
    let alive = true;
    setFiles(null);
    setListingError(null);
    api
      .listJobFiles(state.resolved_job_id)
      .then((list) => {
        if (alive) setFiles(list);
      })
      .catch((e) => {
        if (!alive) return;
        setFiles([]);
        setListingError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : String(e),
        );
      });
    return () => {
      alive = false;
    };
  }, [state.resolved_job_id]);

  const [active, setActive] = useState<BundleTreeSelection | null>(null);
  // Default selection picks: MEETING.md → first per-expert .md → first.
  useEffect(() => {
    if (active !== null || files === null || files.length === 0) return;
    const pick = defaultSelection(files, myExperts);
    if (pick) setActive({ path: pick });
  }, [active, files, myExperts]);

  // Active expert / artifact derived from the selection. The expert is
  // resolved by matching the first path segment against the meeting's
  // picked experts; root + "other" files have no expert and render a
  // neutral banner.
  const activeFile = active
    ? files?.find((f) => f.path === active.path) ?? null
    : null;
  const activeExpert = activeFile
    ? expertForPath(activeFile.path, myExperts)
    : null;
  const activeKind = activeFile ? kindFromPath(activeFile.path) : "md";

  // Fetch per-file content when the selection changes.
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  useEffect(() => {
    if (!activeFile || !state.resolved_job_id) {
      setContent(null);
      return;
    }
    // Image previews render the artifact endpoint directly via <img
    // src=...> (see the "image" arm in renderPreview). Pulling the
    // JPEG/PNG bytes through `.text()` mangles them — and even if it
    // didn't, we don't need the bytes in JS state. Skip the fetch.
    if (kindFromPath(activeFile.path) === "image") {
      setContent(null);
      setContentError(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setContentError(null);
    setContent(null);
    api
      .getJobArtifact(state.resolved_job_id, activeFile.path)
      .then((text) => {
        if (alive) setContent(text);
      })
      .catch((e) => {
        if (!alive) return;
        const msg =
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : String(e);
        setContentError(msg);
        if (e instanceof ApiError && e.status === 404) {
          // Drift between the listing and the per-file fetch — the listing
          // is the source of truth, so this is an unexpected condition.
          console.warn(
            `bundle listing/body drift: ${activeFile.path}: ${msg}`,
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [activeFile, state.resolved_job_id]);

  return (
    <div
      className="recspec-bundle-body"
      style={{
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <div
        className="recspec-bundle-tree"
        style={{
          borderRight: "1px solid var(--rule)",
          background: "var(--paper-2)",
          padding: 16,
          overflow: "auto",
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Bundle
        </div>
        {files === null ? (
          <div className="dim" style={{ fontSize: 12 }}>
            Loading bundle…
          </div>
        ) : files.length === 0 ? (
          <div className="dim" style={{ fontSize: 12 }}>
            {listingError
              ? `Couldn't list this bundle: ${listingError}`
              : "Bundle is empty — finalize may not have completed yet."}
          </div>
        ) : (
          <BundleTree
            files={files}
            experts={myExperts}
            active={active}
            onSelect={setActive}
          />
        )}
      </div>
      <div style={{ overflow: "auto", background: "var(--paper)" }}>
        {activeFile ? (
          <ArtifactBanner
            expert={activeExpert}
            path={activeFile.path}
            kind={activeKind}
            jobId={state.resolved_job_id}
          >
            {loading && <div className="dim">Loading…</div>}
            {!loading &&
              renderPreview(
                activeExpert,
                activeKind,
                content,
                activeFile.path,
                state.resolved_job_id,
              )}
            {contentError && (
              <div
                className="dim"
                style={{ marginTop: 12, fontSize: 12 }}
              >
                (api note: {contentError} — showing placeholder)
              </div>
            )}
          </ArtifactBanner>
        ) : files !== null && files.length > 0 ? (
          <div className="dim" style={{ padding: 32 }}>
            Select an artifact from the bundle tree.
          </div>
        ) : (
          <div className="dim" style={{ padding: 32 }}>
            No artifacts to display.
          </div>
        )}
      </div>
    </div>
  );
}

/** Default-selection preference order: `MEETING.md` → first per-expert
 *  `.md` (in listing order) → first listing entry overall → null. */
export function defaultSelection(
  files: BundleFile[],
  experts: ExpertSummary[],
): string | null {
  if (files.length === 0) return null;
  const root = files.find((f) => f.path === "MEETING.md");
  if (root) return root.path;
  const expertIds = new Set(experts.map((e) => e.id));
  const perExpertMd = files.find((f) => {
    const slash = f.path.indexOf("/");
    if (slash === -1) return false;
    const head = f.path.slice(0, slash);
    return expertIds.has(head) && f.path.endsWith(".md");
  });
  if (perExpertMd) return perExpertMd.path;
  return files[0].path;
}

function expertForPath(
  path: string,
  experts: ExpertSummary[],
): ExpertSummary | null {
  const slash = path.indexOf("/");
  if (slash === -1) return null;
  const head = path.slice(0, slash);
  return experts.find((e) => e.id === head) ?? null;
}

function renderPreview(
  expert: ExpertSummary | null,
  kind: string,
  content: string | null,
  path: string,
  jobId: string | null | undefined,
) {
  // Per-kind renderers want an `expert` for color theming. For root /
  // "other" files we fall back to a neutral colour by passing a synthetic
  // expert-shape with the `ink-3` color token via the MdPreview's
  // existing `color_token` plumbing — the previews degrade gracefully.
  const themingExpert =
    expert ??
    ({
      id: "root",
      display_name: "Bundle",
      short_name: "Bundle",
      monogram: "B",
      color_token: "spec",
      voice_id: "",
      voice_desc: "",
      supported_languages: [],
      finalize_artifacts: [],
      version: 0,
      persona_summary: "",
      default_priority: 0,
    } as unknown as ExpertSummary);
  switch (kind) {
    case "md":
      return (
        <MdPreview
          expert={themingExpert}
          content={content ?? undefined}
        />
      );
    case "csv": {
      const parsed = content ? parseCsv(content) : undefined;
      return (
        <CsvPreview
          expert={themingExpert}
          headers={parsed?.headers}
          rows={parsed?.rows}
        />
      );
    }
    case "mermaid":
      return (
        <MermaidPreview
          expert={themingExpert}
          filename={
            content ? "diagram.mmd" : "diagram.mmd"
          }
          source={content ?? undefined}
        />
      );
    case "diff": {
      const lines = content ? parseDiff(content) : undefined;
      return <DiffPreview lines={lines} />;
    }
    case "image": {
      if (!jobId) {
        return (
          <div className="dim" style={{ fontSize: 12 }}>
            (no job id; image preview unavailable)
          </div>
        );
      }
      const src = `/v1/jobs/${encodeURIComponent(jobId)}/artifact?path=${encodeURIComponent(path)}`;
      return (
        <div style={{ padding: 16, overflow: "auto" }}>
          <img
            src={src}
            alt={path}
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              border: "1px solid var(--rule)",
              borderRadius: 4,
              background: "var(--paper-2)",
            }}
          />
        </div>
      );
    }
    case "json":
      return (
        <MdPreview
          expert={themingExpert}
          content={content ? "```json\n" + content + "\n```" : undefined}
        />
      );
    default:
      return (
        <MdPreview
          expert={themingExpert}
          content={content ?? `(no renderer for kind "${kind}")`}
        />
      );
  }
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (l: string) => l.split(",").map((c) => c.trim());
  return {
    headers: split(lines[0]),
    rows: lines.slice(1).map(split),
  };
}

function parseDiff(text: string): { t: " " | "+" | "-"; text: string }[] {
  return text.split(/\r?\n/).map((l) => {
    const c = l.charAt(0);
    if (c === "+" || c === "-") {
      return { t: c, text: l.slice(1) };
    }
    return { t: " ", text: l.startsWith(" ") ? l.slice(1) : l };
  });
}

function ArtifactBanner({
  expert,
  path,
  kind,
  jobId,
  children,
}: {
  expert: ExpertSummary | null;
  path: string;
  kind: string;
  jobId: string | null;
  children: React.ReactNode;
}) {
  const c = expert?.color_token ?? "spec";
  const display = expert ? expert.display_name : "Bundle";
  const subline = expert
    ? `${expert.short_name}/`
    : "Bundle root";
  return (
    <div>
      <div
        style={{
          padding: "20px 28px",
          background: `linear-gradient(180deg, var(--${c}-tint), var(--paper))`,
          borderBottom: `1px solid color-mix(in oklab, var(--${c}) 25%, var(--rule))`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {expert ? (
            <ExpertMark expert={expert} size={48} />
          ) : (
            <span
              style={{
                width: 48,
                height: 48,
                borderRadius: 7,
                background: "var(--paper-2)",
                border: "1px solid var(--rule)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--ink-3)",
                fontFamily: "var(--mono)",
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              B
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: `var(--${c}-deep)`,
                marginBottom: 2,
              }}
            >
              {subline}
              {expert ? path.slice(expert.id.length + 1) : path}
            </div>
            <div
              className="serif"
              style={{ fontSize: 26, fontWeight: 400, lineHeight: 1.05 }}
            >
              {display}
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
              {kind.toUpperCase()}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn sm" disabled>
              <Icon name="copy" size={12} /> Copy
            </button>
            {jobId && (
              <a
                className="btn sm"
                href={`/v1/jobs/${encodeURIComponent(jobId)}/download`}
                style={{ textDecoration: "none" }}
              >
                <Icon name="download" size={12} /> Download bundle
              </a>
            )}
          </div>
        </div>
      </div>
      <div style={{ padding: "24px 28px" }}>{children}</div>
    </div>
  );
}
